/**
 * TankShell — Tank Redesign foundational chrome.
 *
 * Replaces the prior horizontal top-bar OperatorLayout with the redesign's
 * left-rail + contextual top-bar pattern. Source of truth:
 * `~/Downloads/tank-redesign/scaffold.jsx`.
 *
 * Layout:
 *   ┌─────┬───────────────────────────────────────┐
 *   │     │  TopBar: eyebrow · route / breadcrumb │
 *   │ LR  ├───────────────────────────────────────┤
 *   │     │                                       │
 *   │  ↑  │  Page content                         │
 *   │     │                                       │
 *   │  ↓  │                                       │
 *   │ avt │                                       │
 *   └─────┴───────────────────────────────────────┘
 *
 * - LR (left rail, 56px): brand pill + nav icons + user avatar.
 * - TopBar (48px): mono eyebrow + display title + optional breadcrumb +
 *   slot for per-page actions (counts, kbd hints, controls).
 *
 * Each page can opt into a custom top-bar right-slot via the
 * `useShellTopRight` hook (see ShellContext.jsx).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Squares2X2Icon,
  PencilSquareIcon,
  CalendarIcon,
  UsersIcon,
  ChartBarIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

// HealthIndicator (services-down badge) removed from the topbar — its data
// wasn't reliable enough to show; component file kept on disk for future use.
// import HealthIndicator from '../components/HealthIndicator';
import NotificationBell from '../components/NotificationBell/NotificationBell';
import { usePermissions } from '../hooks';
import { useShellTopRight } from './ShellContext';
import RailFtpStatus from './RailFtpStatus';
import { Kbd, Mono } from '../shared/components/redesign';
import ShortcutsOverlay from './ShortcutsOverlay';
import styles from './TankShell.module.css';

/**
 * Left-rail nav order per design_handoff_tank/scaffold.jsx LeftRail:
 *   Overview · Edit · Matches · Manage · Statistics · Me
 *
 * Edit is the contextual editor surface; it accepts an open photo via
 * the URL hash. When no photo is open, clicking it lands on the most
 * recently viewed photo (or back to Overview if none).
 */
const NAV_ITEMS = [
  { key: '/overview', icon: Squares2X2Icon, label: 'Overview' },
  { key: '/edit', icon: PencilSquareIcon, label: 'Editor' },
  { key: '/matches', icon: CalendarIcon, label: 'Matches' },
  { key: '/manage', icon: UsersIcon, label: 'Manage' },
  { key: '/statistics', icon: ChartBarIcon, label: 'Statistics' },
  { key: '/me', icon: UserCircleIcon, label: 'Me' },
];

/**
 * Convert a route path into the eyebrow text shown on the top bar.
 * Spec README: "Eyebrow (mono, all-caps, e.g. '§ OVERVIEW')". Section
 * numbers belong on the design canvas only — production chrome shows
 * just the route name.
 */
const ROUTE_META = {
  '/overview':   { label: 'Overview' },
  '/edit':       { label: 'Editor' },
  '/matches':    { label: 'Matches' },
  '/manage':     { label: 'Manage' },
  '/statistics': { label: 'Statistics' },
  '/me':         { label: 'Me' },
};

function LeftRail({ currentPath, onNavigate, onLogout, user, perms }) {
  const visibleItems = NAV_ITEMS.filter(
    (it) => !it.requires || perms[it.requires],
  );
  const initials = user?.username
    ? user.username
        .split(/[\s.\-_]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join('')
    : '·';

  return (
    <aside className={styles.rail}>
      <button
        type="button"
        className={styles.brand}
        onClick={() => onNavigate('/overview')}
        title="Tank"
      >
        T
      </button>

      <nav className={styles.nav}>
        {visibleItems.map((it) => {
          const active = currentPath === it.key
            || (it.key === '/manage' && currentPath === '/training');
          const Icon = it.icon;
          return (
            <button
              key={it.key}
              type="button"
              title={it.label}
              className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
              onClick={() => onNavigate(it.key)}
            >
              <Icon className={styles.navIcon} />
            </button>
          );
        })}
      </nav>

      <div className={styles.railSpacer} />

      {/* User pill — kept as identity affordance even though Me is now
          in the rail nav above. Initials shown; clicking goes to /me. */}
      <button
        type="button"
        className={`${styles.avatar} ${currentPath === '/me' ? styles.avatarActive : ''}`}
        onClick={() => onNavigate('/me')}
        title={user?.username || 'Profile'}
      >
        {user?.username ? initials : <UserCircleIcon className={styles.navIcon} />}
      </button>

      {/* FTP / upload status indicator — bottom of rail per handoff README. */}
      <RailFtpStatus />

      <button
        type="button"
        className={styles.logout}
        onClick={onLogout}
        title="Logout"
      >
        <ArrowRightOnRectangleIcon className={styles.navIcon} />
      </button>
    </aside>
  );
}

function TopBar({ currentPath, topBadge, user }) {
  const meta = ROUTE_META[currentPath] || { label: 'Tank' };
  const topRight = useShellTopRight();
  const kbHints = topRight?.kbHints;
  const rate = topRight?.rate ?? topBadge;

  return (
    <header className={styles.topBar}>
      <div className={styles.topBarLeft}>
        <span className={styles.eyebrow}>§ {meta.label.toUpperCase()}</span>
        <span className={styles.dot}>·</span>
        <span className={styles.routeName}>{meta.label}</span>
        {/* Breadcrumb — accepts string (legacy) or array of segments
            `{ label, href? }`. When `href` is set the segment renders
            as a clickable button so users can hop back up the hierarchy
            (e.g. Manage / Company / Profiles → click Company). */}
        {topRight?.breadcrumb && (
          <>
            <span className={styles.slash}>/</span>
            {Array.isArray(topRight.breadcrumb) ? (
              topRight.breadcrumb.map((seg, i, arr) => (
                <span key={i} className={styles.breadcrumb}>
                  {seg.href ? (
                    <button
                      type="button"
                      className={styles.breadcrumbLink}
                      onClick={() => {
                        if (seg.href.startsWith('#')) {
                          window.location.hash = seg.href.slice(1);
                        } else {
                          window.history.pushState({}, '', seg.href);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }
                      }}
                    >
                      {seg.label}
                    </button>
                  ) : (
                    <span>{seg.label}</span>
                  )}
                  {i < arr.length - 1 && (
                    <span className={styles.breadcrumbSep}> · </span>
                  )}
                </span>
              ))
            ) : (
              <span className={styles.breadcrumb}>{topRight.breadcrumb}</span>
            )}
          </>
        )}
      </div>

      <div className={styles.topBarRight}>
        {topRight?.right}
        {kbHints && kbHints.length > 0 && (
          <div className={styles.kbHints}>
            {kbHints.map((h, i) => (
              <span key={i} className={styles.kbHint}>
                {h.keys.map((k, j) => <Kbd key={j}>{k}</Kbd>)}
                <span className={styles.kbHintLabel}>{h.label}</span>
              </span>
            ))}
          </div>
        )}
        {rate && (
          <>
            <span className={styles.dot}>·</span>
            <Mono className={styles.rate}>{rate}</Mono>
          </>
        )}
        <NotificationBell />
        {user?.username && (
          <span className={styles.username}>{user.username}</span>
        )}
      </div>
    </header>
  );
}

export default function TankShell({
  currentPath,
  onNavigate,
  onLogout,
  topBadge,
  user,
  children,
}) {
  const perms = usePermissions();
  const { t } = useTranslation();
  void t;

  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '?') return;
      const tag = e.target?.tagName;
      const editable = e.target?.isContentEditable;
      if (editable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      setHelpOpen((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={styles.shell}>
      <LeftRail
        currentPath={currentPath}
        onNavigate={onNavigate}
        onLogout={onLogout}
        user={user}
        perms={perms}
      />
      <div className={styles.frame}>
        <TopBar currentPath={currentPath} topBadge={topBadge} user={user} />
        <main className={styles.main}>{children}</main>
      </div>
      <ShortcutsOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
